/**
 * DamalsHeute — small "Damals vs. Heute" callout under the date header
 * on /heute. Reuses /api/heritage/landing — same data the landing-page
 * climate tile uses. Adds a one-line emotional climate-change reminder
 * to the daily reading experience without overwhelming the post stream.
 *
 * Click → opens GenerationsStoryModal with a 3-paragraph Gemini-generated
 * narrative (Damals / Heute / Was das bedeutet) — perfect Insta-share
 * material for the current anchor.
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUp, ArrowDown, Clock, Sparkle, BookOpen } from "@phosphor-icons/react";
import GenerationsStoryModal from "@/components/daily/GenerationsStoryModal";

const API = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");

function tone(deltaC) {
  if (deltaC == null) return "#F5F5F5";
  if (deltaC >= 1.0) return "#FF5500";
  if (deltaC >= 0.3) return "#FFC24B";
  if (deltaC <= -0.3) return "#8B7CF6";
  return "#00FA9A";
}

function label(deltaC) {
  if (deltaC == null) return "ähnlich";
  if (deltaC >= 1.5) return "deutlich wärmer";
  if (deltaC >= 0.5) return "wärmer";
  if (deltaC <= -0.5) return "kühler";
  return "ähnlich";
}

export default function DamalsHeute() {
  const [data, setData] = useState(null);
  const [idx, setIdx] = useState(0);
  const [error, setError] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);

  useEffect(() => {
    let mounted = true;
    axios.get(`${API}/api/heritage/landing`)
      .then((r) => { if (mounted) setData(r.data); })
      .catch(() => { if (mounted) setError(true); });
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    if (!data?.anchors?.length) return;
    // Pause rotation while modal is open so the user can read & share
    // the currently-shown anchor without the underlying tile changing.
    if (modalOpen) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % data.anchors.length), 7000);
    return () => clearInterval(t);
  }, [data, modalOpen]);

  if (error || (data && !data.anchors?.length)) return null;
  if (!data) {
    return (
      <div className="border border-white/15 bg-[#0A0A0A] p-4 animate-pulse h-20" />
    );
  }

  const a = data.anchors[idx];
  const Arrow = (a.delta_temp_c ?? 0) >= 0 ? ArrowUp : ArrowDown;
  const t = tone(a.delta_temp_c);
  const lbl = label(a.delta_temp_c);

  return (
    <>
      <button
        type="button"
        onClick={() => setModalOpen(true)}
        className="block w-full text-left border border-white/15 bg-[#121212] p-4 sm:p-5 hover:shadow-[0_0_0_1px_rgba(255,255,255,0.06)] transition-shadow group"
        data-testid="heute-damals"
        style={{ borderLeftWidth: 4, borderLeftColor: "#D9A87C" }}
        aria-label="Generationen-Geschichte öffnen"
      >
        <div className="flex items-start gap-3">
          <Clock size={18} weight="fill" className="text-[#D9A87C] mt-1 shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-[0.22em] font-mono text-[#D9A87C] flex items-center gap-2">
              <Sparkle size={11} weight="fill" /> Damals vs. Heute · letzte 30 Tage
            </div>
            <AnimatePresence mode="wait">
              <motion.div
                key={a.anchor_id}
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -6 }}
                transition={{ duration: 0.35 }}
                className="mt-1.5"
              >
                <p className="text-base sm:text-lg text-white leading-snug">
                  In <strong>{a.label}</strong> ist es heute{" "}
                  <span className="font-bold inline-flex items-baseline gap-0.5" style={{ color: t }}>
                    <Arrow size={14} weight="bold" />
                    {a.delta_temp_c == null ? "—" : (a.delta_temp_c > 0 ? "+" : "") + a.delta_temp_c.toFixed(1)}
                    <span className="text-xs opacity-70 ml-0.5">°C</span>
                  </span>{" "}
                  <span style={{ color: t }} className="font-semibold uppercase tracking-wide text-xs">
                    {lbl}
                  </span>{" "}
                  als an diesem Tag vor 30 Jahren.
                </p>
                <p className="text-xs text-white/55 leading-snug mt-1 font-mono">
                  heute Ø {a.today_temp_c ?? "—"} °C · damals {a.then_temp_c ?? "—"} °C
                  <span className="ml-2">· Daten: ERA5-Archiv (ECMWF)</span>
                </p>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
        <div className="mt-3 flex items-center gap-2">
          {data.anchors.map((_, i) => (
            <span
              key={i}
              className={`h-1 transition-all duration-300 ${
                i === idx ? "w-6 bg-[#7A4A2D]" : "w-1.5 bg-white/10"
              }`}
            />
          ))}
          <span className="ml-auto text-[10px] font-mono uppercase tracking-widest text-[#D9A87C] group-hover:underline inline-flex items-center gap-1">
            <BookOpen size={11} weight="fill" /> Generationen-Geschichte öffnen →
          </span>
        </div>
      </button>
      <GenerationsStoryModal
        open={modalOpen}
        anchor={a}
        onClose={() => setModalOpen(false)}
      />
    </>
  );
}
